import { Button, GenericDialog, H1, InputGroup } from '@/common'
import useI18n from '@/shared/hooks/usei18n'
import { useCallback, useState } from 'react'
import { useUpdateOrganization } from '@/features/organization/hooks/organizations'
import { useSession } from '@/shared/hooks/useSession'
import { motion, AnimatePresence } from 'motion/react'
import { toast } from 'react-hot-toast'

const TOTAL_STEPS = 2

export default function OrganizationSetupDialog({
  isOpen,
  onComplete
}: {
  isOpen: boolean
  onComplete: () => void
}) {
  const { t } = useI18n()
  const { session } = useSession()
  const organizationId = session.organization.id
  const { mutateAsync: updateOrganization, isPending } = useUpdateOrganization(organizationId)

  const [step, setStep] = useState(0)
  const [organizationName, setOrganizationName] = useState(session.organization.name || '')
  const [nameError, setNameError] = useState<string | null>(null)

  const handleContinue = useCallback(async () => {
    const trimmed = organizationName.trim()
    if (!trimmed || trimmed.length < 2) {
      setNameError(t('common:setup.organizationName.error'))
      return
    }
    setNameError(null)
    try {
      await updateOrganization({ name: trimmed })
      setStep(1)
    } catch {
      toast.error(t('common:errors.generic'))
    }
  }, [organizationName, updateOrganization, t])

  const handleGetStarted = useCallback(() => {
    onComplete()
  }, [onComplete])

  return (
    <GenericDialog isOpen={isOpen} close={onComplete} size="3xl" preventCloseOnOutsideClick>
      <div className="flex flex-col items-center py-2">
        <div className="flex-1 flex flex-col items-center justify-center text-center w-full">
          <AnimatePresence mode="wait">
            {step === 0 ? (
              <motion.div
                key="step-0"
                className="flex flex-col items-center gap-6 w-full max-w-[600px]"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.3 }}
              >
                <H1 className="m-0 text-gray-900">{t('common:setup.organizationName.title')}</H1>
                <p className="text-gray-500 m-0 leading-relaxed">
                  {t('common:setup.organizationName.description')}
                </p>
                <div className="w-2/3">
                  <InputGroup
                    id="organization-name"
                    label={t('common:setup.organizationName.label')}
                    value={organizationName}
                    onChange={event => setOrganizationName(event.target.value)}
                    placeholder={t('common:setup.organizationName.placeholder')}
                    error={nameError || undefined}
                  />
                </div>
                <Button
                  onClick={handleContinue}
                  isLoading={isPending}
                  disabled={!organizationName.trim() || organizationName.trim().length < 2}
                >
                  {t('common:setup.organizationName.continue')}
                </Button>
              </motion.div>
            ) : (
              <motion.div
                key="step-1"
                className="flex flex-col items-center gap-6 w-full max-w-[400px]"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.3 }}
              >
                <H1 className="m-0 text-gray-900">{t('common:setup.welcome.title')}</H1>
                <p className="text-gray-500 m-0 leading-relaxed">
                  {t('common:setup.welcome.description')}
                </p>
                <Button onClick={handleGetStarted}>{t('common:setup.welcome.getStarted')}</Button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <div className="flex gap-2 mt-8 pb-2">
          {Array.from({ length: TOTAL_STEPS }).map((_, index) => (
            <motion.div
              key={index}
              className={`size-2 rounded-full ${index === step ? 'bg-green-500' : 'bg-gray-400'}`}
              animate={{
                scale: index === step ? 1.2 : 1,
                opacity: index === step ? 1 : 0.5
              }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            />
          ))}
        </div>
      </div>
    </GenericDialog>
  )
}
